import React, { Children, ReactNode, useEffect, useState } from 'react';
import { DemeterPageElementType, DemeterPageType, MarketIndicatorOutlook } from '../../../../Generated/Raven-Demeter';
import useDynamicDimensions from '../../../Core/Hooks/useDynamicDimensionsHook';
import { EventActionsEnum, EventCategoriesEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import { ComponentHeaderRightSideOptions } from '../../Form/Inputs/ComponentMoreOptionsHorizontalDots';
import { ChartDisplayType } from '../ChartDefinitions';
import ChartInnerWrapper from './ChartInnerWrapper';
import ChartModalWrapper from './ChartModalWrapper';
import styles from './ChartWrapper.module.scss';

export interface IChartHeaderOptions {
    hideTitle?: boolean;
    hidePopoutModal?: boolean;
    showOnlyAsPopout?: boolean;
    hideDownload?: boolean;
    isCsvDownloadAvailable?: boolean;
    isXlsxDownloadAvailable?: boolean;
    isInDashboardEditMode?: boolean;
    isAddToPresentationTemplateAvailable?: boolean;
    isAddToDashboardAvailable?: boolean;
    maximizeMinimizeButton?: ComponentHeaderRightSideOptions.Maximize | ComponentHeaderRightSideOptions.Minimize;
    pageType?: DemeterPageType;
    pageElementType?: DemeterPageElementType;
    chartDisplayTypes?: ChartDisplayType[];
    containerClassName?: string;
    modalAndDownloadTitle?: string;
    handleChartDisplayTypeChange?: (displayType: ChartDisplayType) => void;
    handleMoreOptionsSelection?: (value: ComponentHeaderRightSideOptions) => void;
}

export interface IChartWrapperProps {
    children: JSX.Element;
    name: string; // Internal name of the component.
    customTitle?: string | JSX.Element; // Custom title of the chart to override the default title.
    title?: string; // Title of the chart.
    subtitle?: string; // Sub-title of chart.
    dataSourceTag: string | string[];
    isLoading: boolean; // Set to true if we are loading the chart.
    showPopout?: boolean;
    maxWidthHeightRatio?: number;
    setShowPopout?: (showPopout: boolean) => void;
    headerOptions?: IChartHeaderOptions;
    header?: ReactNode;
    subHeader?: ReactNode;
    rightSide?: ReactNode;
    footer?: ReactNode;
    testId?: string;
    dragIndicatorIcon?: React.ReactNode;
    marketIndicatorOutlook?: MarketIndicatorOutlook;
    track: TrackingEvent;
}

export interface ChartDimensions {
    width: number;
    height: number;
}

const ChartWrapper: React.FC<IChartWrapperProps> = (props: IChartWrapperProps) => {
    const [chartContainerReference, chartContainerDimensions] = useDynamicDimensions();
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        setShowModal(props.showPopout ?? false);
    }, [props.showPopout]);

    useEffect(() => {
        if (!props.setShowPopout || showModal === props.showPopout) {
            if (showModal) {
                loggingService.trackEventWithAnalytics(EventActionsEnum.ModalInteraction, EventCategoriesEnum.PopoutChartModalOpen, props.title, props.name);
            }

            return;
        }

        loggingService.trackEventWithAnalytics(EventActionsEnum.ModalInteraction, EventCategoriesEnum.PopoutChartModalClose, props.title, props.name);
        props.setShowPopout(showModal);
    }, [showModal]);

    if (props.headerOptions?.showOnlyAsPopout && !showModal) {
        return null;
    }

    return (
        <div data-testid={props.testId} ref={chartContainerReference} className={styles.chart_wrapper}>
            <ChartModalWrapper
                name={props.name}
                showModal={showModal}
                setShowModal={(show: boolean) => {
                    setShowModal(show);
                }}
                showOnlyAsPopout={props.headerOptions?.showOnlyAsPopout}
            >
                <ChartInnerWrapper
                    {...props}
                    chartContainerDimensions={chartContainerDimensions}
                    setShowModal={(show: boolean) => {
                        setShowModal(show);
                    }}
                    track={props.track}
                >
                    {Children.only(props.children)}
                </ChartInnerWrapper>
            </ChartModalWrapper>
        </div>
    );
};

export default ChartWrapper;
